import { Container } from "@mui/material";
import { makeStyles } from "@/compat/muiStyles";
import { useRouteMatch } from "@/compat/router";
import { useEffect, useState } from "react";
import { useSnackbar } from "notistack";
import { Check } from "@mui/icons-material";
import PageBase from "../../../../components/PageBase";
import AcknowledgementToolbar from "../../../../components/acknowledgements/AcknowledgementToolbar";
import DismissableToolbar from "../../../../components/DismissableToolbar";
import AcknowledgementDialog from "../../../../components/acknowledgements/AcknowledgeDialog";
import VerifyEmailDialog from "../../../../components/acknowledgements/VerifyEmailDialog";
import DocumentViewer from "../../../../components/acknowledgements/DocumentViewer";
import ConfirmDetails from "../../../../components/acknowledgements/ConfirmDetails";
import AccessDenied from "../../../../components/acknowledgements/AccessDenied";
import FixedButton from "../../../../components/FixedButton";
import safetyNestLogo from "../../../../images/SafetyNest_Purple_Positive_RGB.svg";
import { formatShortMonthDateAndTime } from "../../../../utils/dateTimeUtils";
import { validEmailRegex } from "../../../../utils/regexUtils";
import useRiskAssessmentAcknowledgementDetails from "../../../hooks/queries/useRiskAssessmentAcknowledgementDetails";
import useVerifiedRiskAssessmentAcknowledgement from "../../../hooks/queries/useVerifiedRiskAssessmentAcknowledgement";
import useAcknowledgeRiskAssessment from "../../../hooks/mutations/useAcknowledgeRiskAssessment";
import { ACKNOWLEDGEMENT_STATUS } from "../../../types/riskAssessmentAcknowledgement";

const useStyles = makeStyles((theme) => ({
    marginBottom: {
        marginBottom: theme.spacing(8),
        marginTop: theme.spacing(8),
    },
    check: {
        marginRight: theme.spacing(1),
    },
}));

// SafetyNest's own acknowledge page, reading SafetyNest rather than EWClient. The
// MyWorkNest page at /acknowledge/:reference stays live for links already sent.
export default function AcknowledgeRiskAssessment() {
    const classes = useStyles();

    const { enqueueSnackbar } = useSnackbar();

    const match = useRouteMatch();
    const { reference } = match.params;

    const [email, setEmail] = useState("");
    const [requiresVerification, setRequiresVerification] = useState(true);
    const [isUnauthorized, setIsUnauthorized] = useState(false);
    const [invalidEmail, setInvalidEmail] = useState(false);
    const [readyToVerify, setReadyToVerify] = useState(false);
    const [dismissedReview, setDismissedReview] = useState(false);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [dismissedToolbar, setDismissedToolbar] = useState(false);

    const acknowledge = useAcknowledgeRiskAssessment();
    const { data: details } = useRiskAssessmentAcknowledgementDetails({ reference });
    const {
        data: verified,
        refetch,
        isError: verifyFailed,
    } = useVerifiedRiskAssessmentAcknowledgement({ reference, emailAddress: email, readyToVerify });

    // The API answers an unrecognised email with a 404 rather than a falsy body, so
    // the query erroring is what means "not on the recipient list".
    useEffect(() => {
        if (verifyFailed) setIsUnauthorized(true);
    }, [verifyFailed]);

    const recipient = verified?.acknowledgementRecipient;
    const hasAcknowledged = recipient?.status === ACKNOWLEDGEMENT_STATUS.Acknowledged;

    function verifyEmail() {
        if (!email.match(validEmailRegex)) {
            setInvalidEmail(true);
            return;
        }

        setInvalidEmail(false);
        setRequiresVerification(false);
        setReadyToVerify(true);
        refetch();
    }

    function confirmReview() {
        acknowledge.mutate(
            { reference, emailAddress: email },
            {
                onSuccess: () => {
                    refetch();
                    setConfirmDialogOpen(false);
                    enqueueSnackbar("Review confirmed", { variant: "default" });
                },
                onError: () => {
                    enqueueSnackbar("There was an error confirming your review", { variant: "error" });
                },
            }
        );
    }

    function openConfirmDialog() {
        setConfirmDialogOpen(true);
        setDismissedReview(true);
    }

    function reVerifyEmail() {
        setRequiresVerification(true);
        setIsUnauthorized(false);
        setReadyToVerify(false);
        setEmail("");
    }

    const showDocument = !isUnauthorized && !requiresVerification;

    return (
        <>
            <AcknowledgementToolbar logoUrl={safetyNestLogo} />
            {!dismissedToolbar && showDocument && (
                <>
                    {hasAcknowledged && (
                        <DismissableToolbar
                            onDismiss={() => setDismissedToolbar(true)}
                            title="Review Confirmed"
                            subtitle={recipient?.acknowledgedDate && formatShortMonthDateAndTime(recipient.acknowledgedDate)}
                        />
                    )}
                    {details?.isExpired && !hasAcknowledged && (
                        <DismissableToolbar
                            onDismiss={() => setDismissedToolbar(true)}
                            title="Document Expired"
                            subtitle="A newer version of this risk assessment now exists."
                        />
                    )}
                </>
            )}
            <PageBase>
                <AcknowledgementDialog open={confirmDialogOpen} onConfirm={confirmReview} onCancel={() => setConfirmDialogOpen(false)} />
                <VerifyEmailDialog
                    open={requiresVerification}
                    onVerify={verifyEmail}
                    onCancel={() => {
                        setRequiresVerification(false);
                        setIsUnauthorized(true);
                    }}
                    email={email}
                    setEmail={setEmail}
                    invalidEmail={invalidEmail}
                />
                <Container maxWidth="md" className={classes.marginBottom}>
                    {showDocument && (
                        <>
                            <DocumentViewer fileType={verified?.file?.contentType} fileContents={verified?.file?.fileContents} />
                            {!hasAcknowledged && dismissedReview && (
                                <FixedButton onClick={openConfirmDialog} text="Confirm Review" icon={<Check className={classes.check} />} />
                            )}
                            {!hasAcknowledged && !dismissedReview && !details?.isExpired && (
                                <ConfirmDetails onConfirm={openConfirmDialog} onDismiss={() => setDismissedReview(true)} />
                            )}
                        </>
                    )}
                    {isUnauthorized && !requiresVerification && <AccessDenied verify={reVerifyEmail} />}
                </Container>
            </PageBase>
        </>
    );
}
